import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    FormControlLabel, InputBase, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem, Pagination, Tooltip, Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import { Switch } from '../../components/switch/Switch.tsx';
import { money } from '../../utils/money.ts';
import {
    attractionsService,
    type AdminProduct, type AttractionCollection, type AttractionRules, type Catalogue, type CatalogueFilter,
    type CatalogueGroup, type SyncStatus,
} from '../../services/attractions/attractionsService.ts';
import {
    AttractionsFrame, COLORS, CollectionChip, DEFAULT_RULES, Panel, Tile, canOverride, errorText, failedRules, fmtCount, fmtDateTime,
    fmtDuration, fmtSynced, hiddenText, pillButtonSx, ruleCleared, ruleText,
} from './attractionsShared.tsx';

const PAGE_SIZE = 25;
const POLL_MS = 3000;
const GRID = '56px minmax(260px, 2fr) 1.1fr .8fr .7fr .9fr 1.2fr 124px';

const FILTERS: { key: CatalogueFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'in_app', label: 'In app' },
    { key: 'hidden', label: 'Hidden' },
    { key: 'queue', label: 'Review queue' },
    { key: 'overridden', label: 'Overridden' },
];

/** "Half-day from {city}" as the app shows it in this city. */
const withCity = (title: string, city: string) => title.replace(/\{city\}/g, city || 'the city');

const isLive = (p: AdminProduct) => p.inApp && ruleCleared(p) && !p.hiddenReason;
const isQueued = (p: AdminProduct) => !p.inApp && ruleCleared(p) && !p.hiddenReason;
const isRuleHidden = (p: AdminProduct) => !ruleCleared(p);

const price = (p: AdminProduct): string => {
    if (p.fromPrice == null) return '—';
    if (Number.isInteger(p.fromPrice) && (!p.currency || p.currency === 'EUR')) return `€${p.fromPrice}`;
    return money(p.fromPrice, p.currency);
};

// ---------------------------------------------------------------- rules card

type RuleSwitch = { key: string; label: string; sub: string; on: boolean; apply: (on: boolean, r: AttractionRules) => AttractionRules };

/**
 * The automatic rules as switches, as in the design. Reviews and rating have
 * no on/off of their own in the contract: off is a minimum of 0, on restores
 * the last minimum (or the default). The numbers are edited on the Rules page.
 */
const RulesCard: React.FC<{ rules: AttractionRules | null; saving: boolean; onSave: (r: AttractionRules) => void }> = ({ rules, saving, onSave }) => {
    const lastReviews = useRef(DEFAULT_RULES.minReviews);
    const lastRating = useRef(DEFAULT_RULES.minRating);
    if (rules && rules.minReviews > 0) lastReviews.current = rules.minReviews;
    if (rules && rules.minRating > 0) lastRating.current = rules.minRating;

    const r = rules ?? DEFAULT_RULES;
    const switches: RuleSwitch[] = [
        { key: 'reviews', label: `Hide under ${lastReviews.current} reviews`, sub: 'Too little signal for a family on holiday', on: r.minReviews > 0,
            apply: (on, x) => ({ ...x, minReviews: on ? lastReviews.current : 0 }) },
        { key: 'rating', label: `Hide under ${lastRating.current} stars`, sub: 'Viator average for Cyprus is 4.6', on: r.minRating > 0,
            apply: (on, x) => ({ ...x, minRating: on ? lastRating.current : 0 }) },
        { key: 'nightlife', label: 'Hide nightlife, bars, casinos', sub: 'Off-brand for Vello', on: r.hideNightlife,
            apply: (on, x) => ({ ...x, hideNightlife: on }) },
        { key: 'saturday', label: 'Hide Saturday-only products', sub: 'Shabbat', on: r.hideSaturdayOnly,
            apply: (on, x) => ({ ...x, hideSaturdayOnly: on }) },
        { key: 'dup', label: 'Group near-duplicates', sub: 'Same title words, supplier or meeting point → one group, review once', on: r.groupDuplicates,
            apply: (on, x) => ({ ...x, groupDuplicates: on }) },
        { key: 'multi', label: 'Hide multi-day tours', sub: `Over ${r.multiDayHours} hours`, on: r.hideMultiDay,
            apply: (on, x) => ({ ...x, hideMultiDay: on }) },
        { key: 'transfers', label: 'Hide transfers', sub: 'Airport and hotel transfers are not attractions', on: r.hideTransfers,
            apply: (on, x) => ({ ...x, hideTransfers: on }) },
    ];

    return (
        <Panel sx={{ mt: 2.75, px: 2.25, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Automatic rules</Typography>
                <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>applied on every sync, before anything reaches the review queue</Typography>
                {saving && <CircularProgress size={14} sx={{ ml: 1 }} />}
            </Box>
            {!rules && <Typography sx={{ fontSize: 13, color: COLORS.muted, mt: 1.5 }}>Loading the rules…</Typography>}
            {rules && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px 18px', mt: 1.5 }}>
                    {switches.map(s => (
                        <Box key={s.key} component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: saving ? 'default' : 'pointer', py: 0.75 }}>
                            <Switch
                                size="medium"
                                checked={s.on}
                                disabled={saving}
                                onChange={(_: any, checked: boolean) => onSave(s.apply(checked, rules))}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{s.label}</Typography>
                                <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: '1px' }}>{s.sub}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Panel>
    );
};

// ---------------------------------------------------------------- product row

type RowProps = {
    p: AdminProduct;
    rules: AttractionRules | null;
    collections: AttractionCollection[];
    cityName: string;
    busy: boolean;
    onInApp: (p: AdminProduct, inApp: boolean) => void;
    onHidden: (p: AdminProduct, hidden: boolean) => void;
    onOverride: (p: AdminProduct, override: boolean) => void;
    onCollection: (p: AdminProduct, c: AttractionCollection, add: boolean) => void;
};

/** "Override · fails: Min reviews (3 < 10)" — what an overridden product still fails, or that it passes now. */
const OverrideBadge: React.FC<{ p: AdminProduct; rules: AttractionRules | null }> = ({ p, rules }) => {
    const fails = p.ruleReason ? failedRules(p, rules) : '';
    const by = [p.ruleOverrideBy, p.ruleOverrideAt ? fmtDateTime(p.ruleOverrideAt) : null].filter(Boolean).join(', ');
    return (
        <Tooltip title={`Rules overridden${by ? ` by ${by}` : ''}. The rule stays on for every other product; the nightly sync keeps this one in the app.`}>
            <Box data-override-badge="1" component="span" sx={{
                display: 'inline-block', mt: '5px', fontSize: 11.5, fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7',
                borderRadius: '9px', px: 1, py: '1px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {fails ? `Override · fails: ${fails}` : 'Override · passes the rules now'}
            </Box>
        </Tooltip>
    );
};

const linkButtonSx = { minWidth: 0, p: 0, fontSize: 12, fontWeight: 500, color: COLORS.muted, '&:hover': { bgcolor: 'transparent', color: COLORS.tealDark } };

const ProductRow: React.FC<RowProps> = ({ p, rules, collections, cityName, busy, onInApp, onHidden, onOverride, onCollection }) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const slugs = p.collections ?? [];
    const titleOf = (slug: string) => withCity(collections.find(c => c.slug === slug)?.title ?? slug, cityName);
    const ruleHidden = isRuleHidden(p);
    const ruled = ruleHidden ? ruleText(p, rules) : null;
    const fails = ruleHidden && p.ruleReason !== 'GONE' ? failedRules(p, rules) : '';
    const hidden = hiddenText(p);
    const flags = [...(p.warnings ?? []), ruled && fails ? `Hidden by rule · fails: ${fails}` : ruled, hidden].filter(Boolean) as string[];
    const live = isLive(p);
    const meta = [p.code, p.area, fmtSynced(p.lastSyncedAt)].filter(Boolean).join(' · ');

    return (
        <Box data-product={p.code} sx={{
            display: 'grid', gridTemplateColumns: GRID, gap: 1.5, px: 2.25, py: 1.5, alignItems: 'center',
            borderBottom: `1px solid ${COLORS.rowBorder}`, '&:last-of-type': { borderBottom: 0 }, '& > *': { minWidth: 0 },
        }}>
            <Box sx={{ width: 56, height: 42, borderRadius: '6px', bgcolor: '#D7DEDB', overflow: 'hidden', opacity: live ? 1 : 0.6 }}>
                {p.image?.url && <img src={p.image.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Box sx={{ opacity: live ? 1 : 0.6 }}>
                    {p.bookingUrl ? (
                        <Box component="a" href={p.bookingUrl} target="_blank" rel="noopener noreferrer"
                            sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, color: COLORS.text, textDecoration: 'none', '&:hover': { color: COLORS.tealDark, textDecoration: 'underline' } }}>
                            {p.title}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, color: COLORS.text }}>{p.title}</Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: '3px' }}>{meta}</Typography>
                </Box>
                {flags.map(f => (
                    <Typography key={f} sx={{ fontSize: 11.5, color: COLORS.warn, mt: '3px' }}>{f}</Typography>
                ))}
                {p.ruleOverride && <OverrideBadge p={p} rules={rules} />}
            </Box>
            <Typography sx={{ fontSize: 13, color: COLORS.body, opacity: live ? 1 : 0.6, minWidth: 0, overflowWrap: 'anywhere' }}>{p.supplierName ?? '—'}</Typography>
            <Typography sx={{ fontSize: 13, color: COLORS.text, opacity: live ? 1 : 0.6 }}>
                {p.rating != null ? <b>{p.rating.toFixed(1)}</b> : '—'}{' '}
                {p.reviewCount != null && <span style={{ color: COLORS.muted }}>({fmtCount(p.reviewCount)})</span>}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.text, opacity: live ? 1 : 0.6 }}>{price(p)}</Typography>
            <Typography sx={{ fontSize: 13, color: COLORS.body, opacity: live ? 1 : 0.6 }}>{fmtDuration(p.durationMinutes, p.durationText)}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {slugs.map(s => <CollectionChip key={s} label={titleOf(s)} />)}
                <Tooltip title="Collections">
                    <Box
                        component="button"
                        type="button"
                        aria-label={`Collections for ${p.title}`}
                        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
                        sx={{
                            width: 22, height: 22, borderRadius: '11px', border: '1px dashed #9CA3AF', bgcolor: 'transparent', p: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: COLORS.muted,
                            '&:hover': { borderColor: COLORS.teal, color: COLORS.tealDark },
                        }}
                    >
                        <AddIcon sx={{ fontSize: 14 }} />
                    </Box>
                </Tooltip>
                <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                    {collections.length === 0 && <MenuItem disabled>No collections yet — add one on the Collections page</MenuItem>}
                    {collections.map(c => {
                        const on = slugs.includes(c.slug);
                        return (
                            <MenuItem key={c.id} dense disabled={busy} onClick={() => onCollection(p, c, !on)}>
                                <ListItemIcon sx={{ display: 'flex !important', minWidth: '32px !important' }}>
                                    <Checkbox size="small" edge="start" checked={on} tabIndex={-1} disableRipple color="secondary" sx={{ p: 0 }} />
                                </ListItemIcon>
                                <ListItemText primary={withCity(c.title, cityName)} secondary={c.active ? null : 'Not active'} />
                            </MenuItem>
                        );
                    })}
                </Menu>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, overflowX: 'clip', py: 0.5, pr: 0.25 }}>
                <Tooltip title={ruled ? (canOverride(p) ? `${ruled} — use “Override rules” to show it anyway` : ruled) : hidden ?? ''}>
                    <span>
                        <Switch
                            size="medium"
                            checked={live}
                            disabled={ruleHidden || busy}
                            inputProps={{ 'aria-label': `In app: ${p.title}` }}
                            onChange={(_: any, checked: boolean) => onInApp(p, checked)}
                        />
                    </span>
                </Tooltip>
                {canOverride(p) && (
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => onOverride(p, true)} data-override-action="1"
                        sx={{ height: 26, borderRadius: '13px', borderWidth: 1.5, borderColor: COLORS.teal, color: COLORS.tealDark, fontSize: 12, fontWeight: 700,
                            px: 1.25, whiteSpace: 'nowrap', '&:hover': { borderWidth: 1.5, borderColor: COLORS.tealDark, bgcolor: COLORS.tealSoft } }}>
                        Override rules
                    </Button>
                )}
                {p.ruleOverride && (
                    <Button size="small" disabled={busy} onClick={() => onOverride(p, false)} sx={{ ...linkButtonSx, whiteSpace: 'nowrap' }}>
                        Remove override
                    </Button>
                )}
                {!ruleHidden && !p.inApp && (
                    <Button size="small" disabled={busy} onClick={() => onHidden(p, !p.hiddenReason)} sx={linkButtonSx}>
                        {p.hiddenReason ? 'Unhide' : 'Hide'}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

// ---------------------------------------------------------------- group card

const GroupCard: React.FC<Omit<RowProps, 'p' | 'busy'> & {
    group: CatalogueGroup;
    busyCodes: Set<string>;
    onKeepBest: (g: CatalogueGroup) => void;
    keeping: boolean;
}> = ({ group, busyCodes, onKeepBest, keeping, ...row }) => {
    const [page, setPage] = useState(1);
    const pages = Math.max(1, Math.ceil(group.products.length / PAGE_SIZE));
    useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
    const shown = group.products.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const inApp = group.products.filter(isLive).length;
    const total = group.productCount ?? group.products.length;
    const sub = group.duplicate
        ? `${total} products from Viator · ${inApp} in app`
        : `${total} ${total === 1 ? 'product' : 'products'} · ${inApp} in app`;

    return (
        <Panel sx={{ overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 2.25, py: 1.5, bgcolor: '#FAFBFB', borderBottom: `1px solid ${COLORS.border}` }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: COLORS.text }}>{group.label}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: '1px' }}>{sub}</Typography>
                </Box>
                {group.duplicate && (
                    <>
                        <Box sx={{ fontSize: 12, fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7', borderRadius: '10px', px: 1.25, py: '3px' }}>
                            Near-duplicates
                        </Box>
                        {group.groupId && (
                            <Button
                                size="small" variant="outlined" disabled={keeping} onClick={() => onKeepBest(group)}
                                sx={{ height: 32, borderRadius: '16px', borderWidth: 1.5, borderColor: COLORS.teal, color: COLORS.tealDark, fontWeight: 700, px: 1.5,
                                    '&:hover': { borderWidth: 1.5, borderColor: COLORS.tealDark, bgcolor: COLORS.tealSoft } }}
                            >
                                {keeping ? 'Hiding…' : 'Keep best-rated, hide the rest'}
                            </Button>
                        )}
                    </>
                )}
            </Box>
            <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
                <Box sx={{ minWidth: 960 }}>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: GRID, gap: 1.5, px: 2.25, py: 1, fontSize: 11.5, fontWeight: 700, color: COLORS.muted,
                        textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: `1px solid ${COLORS.rowBorder}`,
                    }}>
                        <span /><span>Product</span><span>Supplier</span><span>Rating</span><span>From</span><span>Duration</span><span>Collections</span>
                        <span style={{ textAlign: 'end' }}>In app</span>
                    </Box>
                    {shown.map(p => <ProductRow key={p.code} p={p} busy={busyCodes.has(p.code)} {...row} />)}
                </Box>
            </Box>
            {pages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.25, py: 1.25, borderTop: `1px solid ${COLORS.rowBorder}` }}>
                    <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
                        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, group.products.length)} of {group.products.length}
                    </Typography>
                    <Pagination size="small" count={pages} page={page} onChange={(_, v) => setPage(v)} />
                </Box>
            )}
        </Panel>
    );
};

// ---------------------------------------------------------------- page

const replaceProduct = (c: Catalogue, next: AdminProduct): Catalogue => {
    const prev = c.groups.flatMap(g => g.products).find(p => p.code === next.code);
    if (!prev) return c;
    const merged = { ...prev, ...next };
    const groups = c.groups.map(g => {
        if (!g.products.some(p => p.code === next.code)) return g;
        const products = g.products.map(p => (p.code === next.code ? { ...p, ...next } : p));
        return { ...g, products, inAppCount: products.filter(isLive).length };
    });
    const dLive = Number(isLive(merged)) - Number(isLive(prev));
    const dQueue = Number(isQueued(merged)) - Number(isQueued(prev));
    const dRemoved = Number(isRuleHidden(merged)) - Number(isRuleHidden(prev));
    return {
        ...c,
        groups,
        tiles: {
            ...c.tiles, liveInApp: c.tiles.liveInApp + dLive, inReviewQueue: c.tiles.inReviewQueue + dQueue,
            removedByRules: c.tiles.removedByRules + dRemoved,
        },
        cities: c.cities.map(city => (city.cityId === c.cityId ? { ...city, liveInApp: city.liveInApp + dLive } : city)),
    };
};

/** Attractions › Catalogue: what Viator sends, the rules, and the In app switch per product. */
const AttractionsCataloguePage = () => {
    const notify = useNotify();
    const [cityId, setCityId] = useState<number | null>(null);
    const [filter, setFilter] = useState<CatalogueFilter>('all');
    const [q, setQ] = useState('');
    const [query, setQuery] = useState('');
    const [data, setData] = useState<Catalogue | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    const [rules, setRules] = useState<AttractionRules | null>(null);
    const [savingRules, setSavingRules] = useState(false);
    const [collections, setCollections] = useState<AttractionCollection[]>([]);
    const [sync, setSync] = useState<SyncStatus | null>(null);
    const [busyCodes, setBusyCodes] = useState<Set<string>>(new Set());
    const [keeping, setKeeping] = useState<string | null>(null);
    const [confirm, setConfirm] = useState<{ p: AdminProduct; override: boolean } | null>(null);
    const [alsoInApp, setAlsoInApp] = useState(true);
    const requestId = useRef(0);

    // Search as you type, a beat after the last key.
    useEffect(() => {
        const t = setTimeout(() => setQuery(q), 350);
        return () => clearTimeout(t);
    }, [q]);

    const load = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        try {
            const next = await attractionsService.catalogue({ cityId, filter, q: query });
            if (id !== requestId.current) return;
            setData(next);
            setFailed(null);
            if (next.lastSync) setSync(s => (s?.running ? s : next.lastSync));
            if (cityId == null && next.cityId != null) setCityId(next.cityId);
        } catch (e) {
            if (id !== requestId.current) return;
            setFailed(errorText(e, 'Could not load the catalogue. Reload the page to try again.'));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [cityId, filter, query]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        attractionsService.rules().then(setRules).catch(() => setRules(DEFAULT_RULES));
        attractionsService.collections().then(setCollections).catch(() => setCollections([]));
    }, []);

    // While a sync runs, ask every few seconds; reload the catalogue when it ends.
    const running = !!sync?.running;
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        if (!running) return;
        let stopped = false;
        const timer = setInterval(async () => {
            try {
                const status = await attractionsService.syncStatus();
                if (stopped) return;
                if (!status.running) {
                    setSync(status);
                    notify(status.message || 'Sync finished', { type: 'success' });
                    loadRef.current();
                }
            } catch {
                // A blip while polling: try again on the next tick.
            }
        }, POLL_MS);
        return () => { stopped = true; clearInterval(timer); };
    }, [running, notify]);

    const startSync = async () => {
        try {
            const status = await attractionsService.startSync();
            setSync({ ...status, running: status?.running ?? true });
            notify('Syncing with Viator… the catalogue reloads when it is done', { type: 'info' });
        } catch (e) {
            notify(errorText(e, 'Could not start the sync'), { type: 'error' });
        }
    };

    const saveRules = async (next: AttractionRules) => {
        const previous = rules;
        setRules(next);
        setSavingRules(true);
        try {
            const stored = await attractionsService.saveRules(next);
            setRules(stored ?? next);
            notify('Rules saved and re-applied', { type: 'success' });
            load();
        } catch (e) {
            setRules(previous);
            notify(errorText(e, 'Could not save the rules'), { type: 'error' });
        } finally {
            setSavingRules(false);
        }
    };

    const markBusy = (code: string, on: boolean) => setBusyCodes(s => {
        const next = new Set(s);
        if (on) next.add(code); else next.delete(code);
        return next;
    });

    const patch = async (p: AdminProduct, body: { inApp?: boolean; hidden?: boolean; ruleOverride?: boolean }, fallback: string): Promise<boolean> => {
        markBusy(p.code, true);
        try {
            const updated = await attractionsService.updateProduct(p.code, body);
            setData(d => (d ? replaceProduct(d, updated ?? { ...p, ...body }) : d));
            return true;
        } catch (e) {
            notify(errorText(e, fallback), { type: 'error' });
            return false;
        } finally {
            markBusy(p.code, false);
        }
    };

    const onInApp = (p: AdminProduct, inApp: boolean) => patch(p, { inApp }, inApp ? 'Could not switch it on' : 'Could not switch it off');
    const onHidden = (p: AdminProduct, hidden: boolean) => patch(p, { hidden }, hidden ? 'Could not hide it' : 'Could not unhide it');

    // Removing an override from a product that passes the rules now changes nothing visible: no need to ask.
    const onOverride = (p: AdminProduct, override: boolean) => {
        if (!override && !p.ruleReason) {
            patch(p, { ruleOverride: false }, 'Could not remove the override');
            return;
        }
        setAlsoInApp(true);
        setConfirm({ p, override });
    };

    const confirmOverride = async () => {
        if (!confirm) return;
        const { p, override } = confirm;
        const ok = override
            ? await patch(p, alsoInApp ? { ruleOverride: true, inApp: true } : { ruleOverride: true }, 'Could not override the rules')
            : await patch(p, { ruleOverride: false }, 'Could not remove the override');
        setConfirm(null);
        if (ok) {
            notify(override
                ? (alsoInApp ? 'Rules overridden — it is in the app now' : 'Rules overridden — switch it In app when ready')
                : 'Override removed — the rule hides it again', { type: 'success' });
        }
    };

    const onCollection = async (p: AdminProduct, c: AttractionCollection, add: boolean) => {
        markBusy(p.code, true);
        try {
            const stored = add
                ? await attractionsService.addToCollection(c.id, p.code)
                : await attractionsService.removeFromCollection(c.id, p.code);
            const member = stored?.productCodes ? stored.productCodes.includes(p.code) : add;
            if (stored) setCollections(list => list.map(x => (x.id === stored.id ? stored : x)));
            const slugs = (p.collections ?? []).filter(s => s !== c.slug);
            setData(d => (d ? replaceProduct(d, { ...p, collections: member ? [...slugs, c.slug] : slugs }) : d));
        } catch (e) {
            notify(errorText(e, 'Could not change the collection'), { type: 'error' });
        } finally {
            markBusy(p.code, false);
        }
    };

    const onKeepBest = async (g: CatalogueGroup) => {
        if (!g.groupId) return;
        setKeeping(g.groupId);
        try {
            const result = await attractionsService.keepBest(g.groupId);
            const kept = g.products.find(p => p.code === result.kept);
            const name = kept ? (kept.title.length > 40 ? `${kept.title.slice(0, 40)}…` : kept.title) : result.kept;
            notify(`Kept “${name}”, hid ${result.hidden?.length ?? 0}`, { type: 'success' });
            await load();
        } catch (e) {
            notify(errorText(e, 'Could not hide the duplicates'), { type: 'error' });
        } finally {
            setKeeping(null);
        }
    };

    const city = data?.cities.find(c => c.cityId === (data?.cityId ?? cityId));
    const cityName = city?.name ?? '';
    const ruleSub = useMemo(() => {
        const r = rules ?? DEFAULT_RULES;
        return [r.minReviews > 0 && 'reviews', r.minRating > 0 && 'rating', r.hideNightlife && 'nightlife', r.hideSaturdayOnly && 'Saturday',
            r.hideMultiDay && 'multi-day', r.hideTransfers && 'transfers'].filter(Boolean).join(', ') || 'no rule is on';
    }, [rules]);

    const syncErrors = (sync?.cities ?? []).filter(c => c.error);

    return (
        <AttractionsFrame
            title="Attractions catalogue"
            subtitle="Viator sends everything for the destination; only what you approve appears in the app"
            actions={(
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 40, border: `1px solid ${COLORS.border}`, borderRadius: '20px', px: 1.75, bgcolor: '#fff', width: { xs: '100%', sm: 280 } }}>
                        <SearchIcon sx={{ fontSize: 18, color: COLORS.muted }} />
                        <InputBase
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Search Viator products"
                            inputProps={{ 'aria-label': 'Search Viator products' }}
                            sx={{ flex: 1, fontSize: 14, '& input': { border: '0 !important', outline: 0, boxShadow: 'none !important', bgcolor: 'transparent !important', p: '0 !important', height: 'auto' } }}
                        />
                    </Box>
                    <Button onClick={startSync} disabled={running} sx={pillButtonSx}
                        startIcon={running ? <CircularProgress size={14} /> : <SyncIcon sx={{ fontSize: 18 }} />}>
                        {running ? 'Syncing…' : 'Sync from Viator'}
                    </Button>
                </>
            )}
        >
            {sync && (sync.running || sync.message) && (
                <Typography data-sync-line="1" sx={{ fontSize: 12.5, color: COLORS.muted, mb: 1.5 }}>
                    {sync.running
                        ? `Syncing with Viator since ${fmtDateTime(sync.startedAt)}…`
                        : `Last sync${sync.finishedAt ? ` ${fmtDateTime(sync.finishedAt)}` : ''}: ${sync.message}`}
                </Typography>
            )}
            {!sync?.running && syncErrors.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {syncErrors.map(c => `${data?.cities.find(x => x.cityId === c.cityId)?.name ?? `City ${c.cityId}`}: ${c.error}`).join(' · ')}
                </Alert>
            )}

            {failed && !data && <Alert severity="error" sx={{ maxWidth: 760 }}>{failed}</Alert>}
            {!failed && !data && <CircularProgress size={24} />}

            {data && (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.75 }}>
                        <Tile label={`From Viator${cityName ? ` · ${cityName}` : ''}`} value={fmtCount(data.tiles.fromViator)} sub="products tagged to this destination" />
                        <Tile label="Removed by rules" value={fmtCount(data.tiles.removedByRules)} sub={ruleSub} />
                        <Tile label="In review queue" value={fmtCount(data.tiles.inReviewQueue)}
                            sub={data.tiles.queueGroups ? `${data.tiles.queueGroups} duplicate ${data.tiles.queueGroups === 1 ? 'group' : 'groups'}, waiting for a decision` : 'waiting for a decision'} />
                        <Tile label="Live in the app" value={fmtCount(data.tiles.liveInApp)} sub={`what customers${cityName ? ` in ${cityName}` : ''} see`} />
                    </Box>

                    <RulesCard rules={rules} saving={savingRules} onSave={saveRules} />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2.75, alignItems: 'center' }}>
                        {data.cities.map(c => {
                            const on = c.cityId === data.cityId;
                            return (
                                <Box key={c.cityId} component="button" type="button" onClick={() => !on && setCityId(c.cityId)}
                                    sx={{
                                        height: 36, borderRadius: '18px', px: 2, display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 14, fontFamily: 'inherit',
                                        cursor: on ? 'default' : 'pointer',
                                        ...(on
                                            ? { bgcolor: COLORS.teal, color: '#fff', fontWeight: 700, border: `1px solid ${COLORS.teal}` }
                                            : { bgcolor: '#fff', color: COLORS.body, border: `1px solid ${COLORS.border}`, '&:hover': { borderColor: '#D1D5DB' } }),
                                    }}>
                                    {c.name}
                                    <span style={on ? { opacity: 0.8, fontWeight: 400 } : { color: '#9CA3AF' }}>{c.liveInApp}</span>
                                </Box>
                            );
                        })}
                        <Box sx={{ flex: 1 }} />
                        {FILTERS.map(f => {
                            const on = f.key === filter;
                            return (
                                <Box key={f.key} component="button" type="button" onClick={() => setFilter(f.key)}
                                    sx={{
                                        height: 34, borderRadius: '8px', px: 1.5, display: 'flex', alignItems: 'center', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
                                        ...(on
                                            ? { bgcolor: COLORS.text, color: '#fff', fontWeight: 700, border: `1px solid ${COLORS.text}` }
                                            : { bgcolor: '#fff', color: COLORS.text, border: `1px solid ${COLORS.border}` }),
                                    }}>
                                    {f.label}{f.key === 'queue' && data.tiles.inReviewQueue > 0 ? ` · ${data.tiles.inReviewQueue}` : ''}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ height: 4, mt: 1.25 }}>{loading && <LinearProgress color="secondary" sx={{ borderRadius: 2 }} />}</Box>
                    {failed && <Alert severity="error" sx={{ mb: 1.5 }}>{failed}</Alert>}

                    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 1.75, opacity: loading ? 0.7 : 1, transition: 'opacity 120ms' }}>
                        {data.groups.filter(g => g.products.length > 0).length === 0 && !loading && (
                            <Box sx={{ bgcolor: '#fff', border: '1px dashed #D1D5DB', borderRadius: '12px', py: 6, px: 3, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.body }}>
                                    {query ? `Nothing matches “${query}”` : data.tiles.fromViator === 0 ? 'Nothing from Viator here yet' : 'No products in this view'}
                                </Typography>
                                {data.tiles.fromViator === 0 && !query && (
                                    <Typography sx={{ fontSize: 13.5, color: COLORS.muted, mt: 0.75 }}>Run “Sync from Viator” to fetch this destination’s products.</Typography>
                                )}
                            </Box>
                        )}
                        {data.groups.filter(g => g.products.length > 0).map(g => (
                            <GroupCard
                                key={`${data.cityId}-${g.groupId ?? 'other'}-${filter}-${query}`}
                                group={g}
                                rules={rules}
                                collections={collections}
                                cityName={cityName}
                                busyCodes={busyCodes}
                                onInApp={onInApp}
                                onHidden={onHidden}
                                onOverride={onOverride}
                                onCollection={onCollection}
                                onKeepBest={onKeepBest}
                                keeping={keeping === g.groupId}
                            />
                        ))}
                    </Box>

                    <Typography sx={{ mt: 2.75, fontSize: 12.5, color: COLORS.muted, lineHeight: 1.5, maxWidth: 900 }}>
                        How it works: Viator’s Partner API returns every product tagged to a destination. Each sync runs the automatic rules, groups
                        near-identical products by title similarity, supplier and meeting point, and puts new ones in the review queue. Only products
                        switched to <b>In app</b> are shown to customers; prices and availability still come live from Viator. Collections are Vello’s own tags.
                        A product a rule hides can still be shown with <b>Override rules</b>: the rule stays on for everything else, and the sync keeps
                        the overridden product in the app (it still shows which rule it fails).
                    </Typography>
                </>
            )}

            {confirm && (
                <Dialog open onClose={busyCodes.has(confirm.p.code) ? undefined : () => setConfirm(null)} maxWidth="sm" fullWidth
                    PaperProps={{ sx: { borderRadius: '16px' } }}>
                    <DialogTitle sx={{ fontWeight: 700 }}>
                        {confirm.override ? 'Override the rules for this product?' : 'Remove the override?'}
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{confirm.p.title}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: '2px' }}>{confirm.p.code}{confirm.p.supplierName ? ` · ${confirm.p.supplierName}` : ''}</Typography>
                        <Box data-confirm-fails="1" sx={{ mt: 1.75, px: 1.5, py: 1.25, borderRadius: '10px', bgcolor: '#FEF3C7', color: '#92400E', fontSize: 13.5 }}>
                            Fails: <b>{failedRules(confirm.p, rules) || '—'}</b>
                        </Box>
                        <DialogContentText sx={{ mt: 1.75, fontSize: 14 }}>
                            {confirm.override
                                ? 'The rule stays on for every other product. This one can be switched In app, gets its details and meeting point from Viator now, and the nightly sync keeps it in the app while it still fails the rule.'
                                : `The rule will hide it again${confirm.p.inApp ? ' and switch it off in the app at once' : ''}.`}
                        </DialogContentText>
                        {confirm.override && (
                            <FormControlLabel sx={{ mt: 1 }}
                                control={<Checkbox color="secondary" checked={alsoInApp} onChange={e => setAlsoInApp(e.target.checked)} />}
                                label={<Typography sx={{ fontSize: 14 }}>Also switch it In app now</Typography>} />
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setConfirm(null)} disabled={busyCodes.has(confirm.p.code)} sx={{ color: COLORS.text }}>Cancel</Button>
                        <Button variant="contained" color="secondary" onClick={confirmOverride} disabled={busyCodes.has(confirm.p.code)}
                            sx={{ color: '#fff', borderRadius: '20px', px: 2.5 }}>
                            {busyCodes.has(confirm.p.code) ? 'Saving…' : confirm.override ? 'Override rules' : 'Remove override'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </AttractionsFrame>
    );
};

export default AttractionsCataloguePage;
